import { SBBox, SBGrid, SBLoading } from "modules/Commons";
import SBEmptyState from "modules/Commons/SBLayouts/SBEmptyState";
import { useSnackbar } from "modules/Commons/SBOverlays/SBSnackbar";
import { useGlobalInventoryContext } from "modules/Inventory/components/SBGlobalInventoryInfoModal";
import { useOptimizationRequester } from "modules/Optimizations/hooks/useOptimizationRequester";
import { useDistributionOrderRequester } from "modules/DistributionOrder/hooks/useDistributionOrderRequester";
import DistributionOrderConfirmation from "modules/DistributionOrder/components/DistributionOrderConfirmation";
import DistributionEmptyState from "modules/Distributions/components/DistributionEmptyState";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import DistributionSummarization from "./DistributionSummarization";
import DistributionViewTable, { getID } from "./DistributionViewTable";
import { useStyles } from "./style";
import SBComposedFilter from "views/Commons/SBComposedFilter";
import { IComposedFilter } from "domain/Utils";
import { IDistributionOrder } from "domain/Inventory/Models/IDistributionOrder";
import { IMovementPlanSummary } from "domain/Inventory/Models/IMovementPlanSummary";
import { useSystemFilterContext } from "contexts/SystemFilterContext";
import { useDateStorageCache } from "hooks/useDateStorageCache";
import SBContextDateInfo from "views/Commons/SBContextDateInfo";
import { DateTime } from "luxon";
import { useNotificationsRequester } from "modules/Notifications/hooks/useNotificationsRequester";
import { useMixpanelContext } from "contexts/MixpanelContext";
import { useCSV } from "hooks/useCSV";
import { SBTranslate } from "i18n";
import DistributionUploaderHelper from "./DistributionUploaderHelper";
import { IDistributionCSVErrors } from "./DistributionUploaderHelper/DistributionCSVHandler";

export default () => {
	const { t } = useTranslation();
	const [items, setItems] = useState<any[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [summaryBase, setSummaryBase] = useState<IMovementPlanSummary | undefined>();
	const [skip, setSkip] = useState(0);
	const take = 30;
	const [isFetchingMore, setIsFetchingMore] = useState(false);
	const hasMore = skip + take < totalCount;
	const [loading, setLoading] = useState(false);
	const [loadingExport, setLoadingExport] = useState(false);
	const [availableDates, setAvailableDates] = useState<string[]>([]);
	const [isImportModalOpen, setIsImportModalOpen] = useState(false);
	const [modalErrors, setModalErrors] = useState<IDistributionCSVErrors[]>([]);
	const [csvProcessed, setCsvProcessed] = useState(false);
	const [tableKey, setTableKey] = useState(0);
	const [orderDialogOpen, setOrderDialogOpen] = useState(false);
	const [orderLoading, setOrderLoading] = useState(false);
	const classes = useStyles();
	const tableRef = useRef<any>();
	const { downloadCSV } = useCSV();
	const { error, success } = useSnackbar();

	const optimizationRequester = useOptimizationRequester();
	const distributionOrderRequester = useDistributionOrderRequester();
	const notificationsRequester = useNotificationsRequester();
	const { context, filtersContext, setFiltersContextState, notCurrentContext } = useSystemFilterContext();
	const { getDateItem, setDateItem } = useDateStorageCache();
	const { trackHistoryChange, trackExport } = useMixpanelContext();
	const { openMenu, isOpen } = useContext(useGlobalInventoryContext);

	const handleOpenInventory = (id: number) => {
		openMenu(id);
	};

	const hasAnyPlans = useMemo(() => items?.length > 0, [items]);

	const fetchPlans = async () => {
		setLoading(true);
		setSkip(0);

		optimizationRequester.distributeCount(filtersContext?.items ?? [])
			.then(c => {
				if (c) {
					setTotalCount(c.totalCount);
					setSummaryBase(c);
				} else {
					setTotalCount(0);
					setSummaryBase(undefined);
				}
			});

		optimizationRequester.distribute(filtersContext?.items ?? [], 0, take)
			.then((data) => setItems(data))
			.finally(() => setLoading(false));
	};

	const fetchMorePlans = async () => {
		if (isFetchingMore || !hasMore) return;
		setIsFetchingMore(true);
		const newSkip = skip + take;
		setSkip(newSkip);
		optimizationRequester.distribute(filtersContext?.items ?? [], newSkip, take)
			.then((data) => setItems([...items, ...data]))
			.finally(() => setIsFetchingMore(false));
	};

	const handleScroll = (event: any) => {
		const target = event.target;
		if (target.scrollHeight - target.scrollTop <= target.clientHeight + 100) {
			fetchMorePlans();
		}
	};

	const getAvailableDates = async () => {
		const result = await notificationsRequester.homeStatisticsDates();
		if (result) setAvailableDates(result);
	};

	const handleOnApply = async (obj: IComposedFilter) => {
		obj.picker?.date && setDateItem(obj.picker.date.toFormat("yyyy-MM-dd"));

		setLoading(true);
		setSkip(0);
		if (obj.filter) setFiltersContextState(obj.filter);
		else setFiltersContextState([]);

		trackHistoryChange({ date: getDateItem() });

		const filter = obj.filter ?? [];

		optimizationRequester.distributeCount(filter)
			.then(c => {
				if (c) {
					setTotalCount(c.totalCount);
					setSummaryBase(c);
				} else {
					setTotalCount(0);
					setSummaryBase(undefined);
				}
			});

		optimizationRequester.distribute(filter, 0, take)
			.then((data) => setItems(data))
			.finally(() => setLoading(false));
	};

	const handleExport = async () => {
		setLoadingExport(true);
		trackExport({ date: getDateItem() });

		let itemsToExport = items;
		if (totalCount && items.length < totalCount) {
			itemsToExport = await optimizationRequester.distribute(filtersContext?.items ?? [], 0, totalCount);
		}

		const csv = tableRef.current.exportCSV(itemsToExport, []);
		const fileName = `${t("suggestedDistribution")}_${DateTime.now().toISODate()}`;
		downloadCSV({ csv, title: fileName });

		setLoadingExport(false);
	};

	const sendDistributionToOrders = () => {
		const selectionState = tableRef.current.selecteds();
		const selectedIndices = Object.keys(selectionState);

		if (selectedIndices.length === 0) {
			error(SBTranslate("noRowsSelectedForOrder"));
			return;
		}

		const eligiblePlans: any[] = [];
		for (const index of selectedIndices) {
			const parts = index.split(".");
			if (parts.length === 2) {
				const groupIdx = parseInt(parts[0], 10);
				const planIdx = parseInt(parts[1], 10);
				if (items[groupIdx]?.plans?.[planIdx]) {
					eligiblePlans.push(items[groupIdx].plans[planIdx]);
				}
			}
		}

		if (eligiblePlans.length === 0) {
			error(SBTranslate("noRowsSelectedForOrder"));
			return;
		}

		const orders: Partial<IDistributionOrder>[] = eligiblePlans.map((plan: any) => {
			const group = items.find((g: any) =>
				(g.plans ?? []).some((p: any) => getID(p) === getID(plan))
			);
			const productClientID = plan.product?.clientID ?? group?.inventory?.product?.clientID;
			const sourceStoreClientID = group?.source?.clientID ?? group?.inventory?.store?.clientID;
			const destinationStoreClientID = plan.store?.clientID;
			const quantity = Math.max(0, (+plan.amount) + (+plan.modifier || 0));

			return {
				guid: plan.uuid,
				productClientID,
				sourceStoreClientID,
				destinationStoreClientID,
				created: plan.cycle?.request,
				expected: plan.cycle?.delivery,
				supplyBrainRecommendation: plan.amount,
				quantity,
			};
		}).filter((o: any) => o.quantity > 0);

		if (orders.length === 0) {
			error(SBTranslate("noValidOrdersToCreate"));
			return;
		}

		setOrderLoading(true);
		distributionOrderRequester.createOrders(orders)
			.then((count: number) => {
				const failed = orders.length - count;
				if (failed === 0) {
					success(SBTranslate("ordersCreatedSuccess"));
				} else {
					error(SBTranslate("ordersPartialError", { count: failed }));
				}
				setOrderDialogOpen(false);
				fetchPlans();
			})
			.catch(() => {
				error(SBTranslate("ordersCreatedError"));
				setOrderDialogOpen(false);
			})
			.finally(() => setOrderLoading(false));
	};

	const downloadTemplateModifiersCSV = async () => {
		setLoadingExport(true);

		let itemsToExport = items;
		if (totalCount && items.length < totalCount) {
			itemsToExport = await optimizationRequester.distribute(filtersContext?.items ?? [], 0, totalCount);
		}

		let csvHeaders = [
			SBTranslate("productCode"),
			SBTranslate("storeCode"),
			SBTranslate("modifier")
		];

		const csvHeaderRow = csvHeaders.join(SBTranslate("csvSeparator")) + "\n";
		const csvRows = itemsToExport.flatMap((item: any) => {
			return (item.plans || []).map((plan: any) => {
				return [
					item.inventory.product.clientID,
					plan.store?.clientID || plan.inventory?.store?.clientID || "",
					plan.modifier || 0
				].join(SBTranslate("csvSeparator"));
			});
		}).join("\n");

		const fullCsv = csvHeaderRow + csvRows;

		downloadCSV({ csv: fullCsv, title: SBTranslate("exportTemplate") });
		setLoadingExport(false);
	};

	useEffect(() => {
		if (csvProcessed) {
			setCsvProcessed(false);
			setTableKey((prev) => prev + 1);
		}
	}, [csvProcessed]);

	useEffect(() => {
		if (context) fetchPlans();
	}, [context]);

	useEffect(() => {
		getAvailableDates();
	}, []);

	return (
		<SBBox className={classes.mainBox}>
			<SBBox display="flex" justifyContent="end">
				<SBComposedFilter.Base onApply={handleOnApply}>
					<SBComposedFilter.Picker date={getDateItem() && DateTime.fromISO(getDateItem())} range={false} showConfigOptions={false} showDateGranularityOptions={false} availableDates={availableDates} />
					<SBComposedFilter.Filter />
					<SBComposedFilter.Applicator />
					<SBComposedFilter.Actions
						disabled={!hasAnyPlans}
						loading={loadingExport}
						actions={[
							{
								icon: { name: "upload" },
								title: "importModifiers",
								onClick: () => {
									setIsImportModalOpen(true);
								},
							},
							{
								icon: { name: "download" },
								title: "exportTemplate",
								onClick: downloadTemplateModifiersCSV,
							},
						]}
					/>
					<SBComposedFilter.Actions
						disabled={!hasAnyPlans}
						loading={loadingExport}
						actions={[
							{
								icon: { name: "shopping-cart" },
								title: "createOrders",
								onClick: () => setOrderDialogOpen(true),
							},
							{
								title: "exportCSV",
								icon: { name: "download" },
								onClick: handleExport,
							},
						]}
					/>
				</SBComposedFilter.Base>
			</SBBox>

			{notCurrentContext() ? <SBGrid container>
				<SBContextDateInfo context={DateTime.fromISO(getDateItem())} />
			</SBGrid> : <></>}

			<SBLoading loading={loading}>
				<SBEmptyState show={hasAnyPlans} emptyState={<DistributionEmptyState />}>
					<SBBox my={2}>
						<DistributionSummarization items={items} summaryBase={summaryBase} />
						<DistributionViewTable key={tableKey} items={items} tableRef={tableRef} isOpen={isOpen} onOpenInventory={handleOpenInventory} onChangeAny={(updatedItems: any[]) => setItems(updatedItems)} totalCount={totalCount} handleScroll={handleScroll} />
					</SBBox>
				</SBEmptyState>
			</SBLoading>

			<DistributionUploaderHelper
				open={isImportModalOpen}
				errors={modalErrors}
				handleClose={() => setIsImportModalOpen(false)}
				items={items}
				setItems={setItems}
				setCsvProcessed={setCsvProcessed}
				setModalErrors={setModalErrors}
			/>

			<DistributionOrderConfirmation
				open={orderDialogOpen}
				loading={orderLoading}
				onConfirm={sendDistributionToOrders}
				onClose={() => setOrderDialogOpen(false)}
				onCancel={() => setOrderDialogOpen(false)}
			/>
		</SBBox>
	);
};
